import { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import showcaseBg from './images/danza/showcase-bg.jpg';

// Seksi pemilih warna ala danza.co.id: latar studio terang, nama model dan
// deskripsi di kiri atas, mobil besar di tengah yang berganti dengan crossfade,
// swatch warna berbentuk pil di bawahnya, lalu tiga angka spesifikasi yang
// beranimasi naik saat seksi terlihat.
//
// props:
//   title        nama model (dipakai bila tidak ada logo)
//   logo         gambar wordmark model (opsional)
//   kicker       label di samping nama, default "Exterior"
//   description  paragraf singkat
//   colors       [{ label, start, end, image }]  start/end = warna gradasi swatch
//   defaultColor label warna awal (opsional)
//   imageRatio   tinggi/lebar gambar mobil dalam persen, default 55.3 (1300x719)
//   stats        [{ label, value, unit, decimals }] maksimal 3
//   cta          { label, href } (opsional)

const TEXT = '#293241';

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function useCountUp(target, started, duration = 1400) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!started) return undefined;
    if (prefersReducedMotion()) {
      setValue(target);
      return undefined;
    }
    let frame;
    const t0 = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) frame = requestAnimationFrame(step);
      else setValue(target);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target, started, duration]);

  return value;
}

function StatItem({ stat, started }) {
  const animated = useCountUp(stat.value, started);
  const decimals = stat.decimals || 0;
  const formatted = animated.toLocaleString('id-ID', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: { xs: '0.72rem', md: '0.82rem' },
          fontWeight: 300,
          lineHeight: 1.4,
          color: TEXT,
        }}
      >
        {stat.label}
      </Typography>
      <Typography
        sx={{
          mt: 0.3,
          fontSize: { xs: '1.55rem', sm: '2rem', md: '2.6rem' },
          fontWeight: 800,
          lineHeight: 1.1,
          letterSpacing: -0.5,
          color: TEXT,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {formatted}
        <Box
          component='span'
          sx={{
            ml: 0.6,
            fontSize: { xs: '0.75rem', md: '0.9rem' },
            fontWeight: 600,
            letterSpacing: 0,
          }}
        >
          {stat.unit}
        </Box>
      </Typography>
    </Box>
  );
}

function CarColorShowcase({
  title,
  logo,
  kicker = 'Exterior',
  description,
  colors,
  defaultColor,
  imageRatio = 55.3,
  stats = [],
  cta,
  id,
}) {
  const sectionRef = useRef(null);
  const [selected, setSelected] = useState(
    () => colors.find((c) => c.label === defaultColor) || colors[0],
  );
  const [started, setStarted] = useState(false);

  // Mulai animasi angka saat seksi masuk layar.
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setStarted(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const ctaButton = cta ? (
    <Button
      href={cta.href}
      target={cta.external ? '_blank' : undefined}
      rel={cta.external ? 'noopener noreferrer' : undefined}
      variant='outlined'
      sx={{
        borderRadius: 0,
        px: 3,
        py: 1,
        textTransform: 'uppercase',
        letterSpacing: 1,
        fontSize: '0.78rem',
        fontWeight: 700,
        color: TEXT,
        borderColor: 'rgba(41,50,65,0.55)',
        backgroundColor: 'rgba(255,255,255,0.35)',
        '&:hover': { borderColor: TEXT, backgroundColor: 'rgba(255,255,255,0.7)' },
      }}
    >
      {cta.label}
    </Button>
  ) : null;

  return (
    <Box
      ref={sectionRef}
      id={id}
      component='section'
      aria-label={`Pilihan warna ${title}`}
      sx={{
        position: 'relative',
        minHeight: { md: '100vh' },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        color: TEXT,
        backgroundColor: '#e9eaec',
        backgroundImage: `url(${showcaseBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* teks kiri atas */}
      <Box
        sx={{
          position: { xs: 'relative', md: 'absolute' },
          top: { md: '6.5vw' },
          left: { md: '7.3vw' },
          zIndex: 2,
          px: { xs: 2.5, sm: 4, md: 0 },
          pt: { xs: 6, sm: 7, md: 0 },
          maxWidth: { xs: '100%', md: '28vw' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.2, md: 1.6 }, flexWrap: 'wrap' }}>
          {logo ? (
            <Box
              component='img'
              src={logo}
              alt={title}
              sx={{
                width: { xs: '46vw', sm: '34vw', md: '13.3vw' },
                maxWidth: 320,
                height: 'auto',
                display: 'block',
              }}
            />
          ) : (
            <Typography
              component='h2'
              sx={{ fontSize: { xs: '1.5rem', md: '1.9vw' }, fontWeight: 800, lineHeight: 1.1 }}
            >
              {title}
            </Typography>
          )}
          <Box component='span' sx={{ opacity: 0.35, fontSize: { xs: '1.3rem', md: '1.6vw' }, fontWeight: 200 }}>
            |
          </Box>
          <Typography sx={{ fontSize: { xs: '1.15rem', md: '1.4vw' }, fontWeight: 400 }}>
            {kicker}
          </Typography>
        </Box>
        {description && (
          <Typography
            sx={{
              mt: { xs: 1.5, md: '1.4vw' },
              fontSize: { xs: '0.95rem', md: '0.95vw' },
              lineHeight: 1.6,
              color: 'rgba(41,50,65,0.88)',
            }}
          >
            {description}
          </Typography>
        )}
      </Box>

      {/* panggung mobil */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          pt: { xs: 3, md: '13vw' },
          px: { xs: 1, md: 0 },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: { xs: '100%', md: '62.5vw' },
            maxWidth: 1200,
            '&::before': { content: '""', display: 'block', paddingTop: `${imageRatio}%` },
          }}
        >
          {colors.map((color) => {
            const active = color.label === selected.label;
            return (
              <Box
                key={color.label}
                component='img'
                src={color.image}
                alt={`${title} warna ${color.label}`}
                loading={active ? 'eager' : 'lazy'}
                aria-hidden={!active}
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  objectPosition: 'center bottom',
                  opacity: active ? 1 : 0,
                  transform: active ? 'scale(1)' : 'scale(0.985)',
                  transition: 'opacity 0.45s ease, transform 0.45s ease',
                  pointerEvents: 'none',
                  filter: 'drop-shadow(0 28px 26px rgba(16,24,40,0.18))',
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* kontrol: swatch + angka + tombol */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: { xs: 2, md: 0 },
          pt: { xs: 2.5, md: '1vw' },
          pb: { xs: 4, md: '3.4vw' },
        }}
      >
        <Box
          role='radiogroup'
          aria-label='Pilihan warna eksterior'
          sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexWrap: 'wrap' }}
        >
          {colors.map((color) => {
            const active = color.label === selected.label;
            return (
              <Box
                key={color.label}
                component='button'
                type='button'
                role='radio'
                aria-checked={active}
                aria-label={color.label}
                title={color.label}
                onClick={() => setSelected(color)}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  px: { xs: 0.8, sm: 1.2, md: '1.1vw' },
                  minWidth: { xs: 56, sm: 72, md: 90 },
                  '&:focus-visible > div': { boxShadow: '0 0 0 3px rgba(45,108,223,0.45)' },
                }}
              >
                <Box
                  sx={{
                    width: active ? { xs: 50, md: 64 } : { xs: 26, md: 32 },
                    height: { xs: 26, md: 32 },
                    borderRadius: 999,
                    background: `linear-gradient(135deg, ${color.start} 0%, ${color.end} 100%)`,
                    boxShadow: active
                      ? '0 8px 16px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(0,0,0,0.08)'
                      : 'inset 0 0 0 1px rgba(0,0,0,0.1)',
                    transition: 'width 0.25s ease, box-shadow 0.25s ease',
                  }}
                />
                <Typography
                  sx={{
                    mt: 0.8,
                    height: 18,
                    fontSize: { xs: '0.74rem', md: '0.85rem' },
                    whiteSpace: 'nowrap',
                    color: TEXT,
                    opacity: active ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                  }}
                >
                  {color.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {(stats.length > 0 || cta) && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mt: { xs: 3, md: '2.2vw' },
            }}
          >
            {stats.length > 0 && (
              <Box
                sx={{
                  width: { xs: '100%', md: '52vw' },
                  maxWidth: 1000,
                  display: 'grid',
                  gridTemplateColumns: `repeat(${Math.min(stats.length, 3)}, 1fr)`,
                  gap: { xs: 1.5, md: 3 },
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                {stats.slice(0, 3).map((stat) => (
                  <StatItem key={stat.label} stat={stat} started={started} />
                ))}
              </Box>
            )}

            {cta && (
              <>
                <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', left: '7.3vw', bottom: 0 }}>
                  {ctaButton}
                </Box>
                <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 3 }}>{ctaButton}</Box>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default CarColorShowcase;
